// Game Day: one matchup, and the day around it (MATCHUP.md, DIRECTION U22,
// B-317). The score card on top; under it, while a slot of mine still cannot
// score, the lineup check; then head to head, and beside it the games by
// kickoff, the league's other scores, the plays and the talk. The tab was
// Matchup, at the same addresses. Drawn through the kit (LOOKS.md Part 7);
// each side's color reaches the header as data.

import { useEffect, useRef, useState } from 'react';
import { TeamBanner, hasBanner } from '../kit/TeamBanner';
import { TeamFigure, teamFace } from '../kit/TeamFigure';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  formatInZone,
  playsTheField,
  readableColor,
  settledOn,
  valueOf,
  type FaceMood,
  type MatchupRowView,
  type MatchupView,
} from '@fsp/domain';
import { diff as diffOf, num } from './shared/notComputed';
import {
  useHome,
  useLeagueBase,
  useMatchup,
  useMyGames,
  useMyMatchupId,
  useReaderZone,
  useScoreboard,
} from '../data/hooks';
import { useViewedPeriod } from '../data/period';
import { Detail, useAtLeast } from '../data/detail';
import { useTools } from '../data/tools';
import { useDelight } from '../delight/useDelight';
import {
  DeltaChip,
  GameLabel,
  LiveDot,
  PositionPill,
  ProTeamCode,
  ScoreNumber,
  StatusBadge,
  TeamMark,
} from '../kit';
import { Why } from '../kit/Why';
import { Icon } from '../kit/Icon';
import { Button, Caption, Colored, Ink, Segments, SplitBar, Text } from '../kit/ui';
import { useTier } from '../shell/useTier';
import { useAccountPrefs } from '../data/accountPrefs';
import { HeadToHead } from './shared/HeadToHead';
import { useChatDrawer } from '../shell/chatDrawer';
import { gameLabel } from '../shell/ShareTray';
import '../styles/screens/league-screens.css';
import { SplitScreen } from '../templates/SplitScreen';
import { TitleCrown } from './shared/AwardKit';
import { Proj, ProjectionWhy, projectionNote } from './shared/Projection';
import { PointsWhy } from './shared/PointsWhy';
import { Term } from '../kit/Why';
import { GLOSSARY } from './shared/glossary';
import { PlayerCard, type PlayerCardData } from './shared/PlayerCard';
import { StatsNote } from './shared/StatsNote';
import { storageKey } from '../data/storage';
import { FromLeague } from './shared/FromLeague';
import { AdjustPointsSheet } from './shared/AdjustPointsSheet';
import { useActiveLeague } from '../data/LeaguesProvider';
import { useLeagueSource } from '../data/leagueSource';
import { PlayerName, TeamName } from './shared/Names';
import { HealthBar } from './shared/HealthBar';
import { GameSlate } from './gameDay/GameSlate';
import { LeagueScores } from './gameDay/LeagueScores';
import { FieldGameDay } from './gameDay/FieldGameDay';
import { GameDayFlip } from './gameDay/GameDayFlip';
import '../styles/screens/game-day.css';

export function MatchupScreen() {
  const params = useParams();
  // The week on screen (D4, B-216): my matchup that week, from the schedule.
  const { period } = useViewedPeriod();
  // A LEAGUE WITH NO OPPONENTS (Q4, Q5, B-360, B-361) has my week against
  // the field here instead, from the scoreboard and my lineup.
  const format = useActiveLeague()?.league.format.id;
  if (format !== undefined && playsTheField(format) && params.id === undefined) {
    return (
      <GameDayFlip>
        <FieldOrGames period={period} />
      </GameDayFlip>
    );
  }
  // MY OWN GAME DAY FLIPS TO MY NEXT LEAGUE'S (F7); another team's game does not.
  if (params.id === undefined) {
    return (
      <GameDayFlip>
        <MatchupOf period={period} />
      </GameDayFlip>
    );
  }
  return <MatchupOf period={period} />;
}

/**
 * THE WEEK DECIDES, NOT THE FORMAT: an all-play league plays its regular
 * weeks against the field and its bracket head to head, so a week the
 * scoreboard serves no field for is a week of games.
 */
function FieldOrGames({ period }: { period: number | undefined }) {
  const board = valueOf(useScoreboard(period));
  if (board !== null && board.field === null) return <MatchupOf period={period} />;
  return <FieldGameDay period={period} />;
}

function MatchupOf({ period }: { period: number | undefined }) {
  const params = useParams();
  const mine = useMyMatchupId(period);
  // TWO OPPONENTS A WEEK (Q6, B-614): my own Game Day is one of my two games
  // at a time, the second by `?game=2`, chosen above it; a game opened by its
  // id is that game alone.
  const games = useMyGames(period);
  const [search, setSearch] = useSearchParams();
  const second = params.id === undefined && search.get('game') === '2' ? games[1] : undefined;
  const id = params.id ?? second?.id ?? mine;
  const choose = (which: string) =>
    setSearch(
      (now) => {
        const next = new URLSearchParams(now);
        if (which === games[1]?.id) next.set('game', '2');
        else next.delete('game');
        return next;
      },
      { replace: true },
    );
  // E12: `null` INSIDE a ready snapshot is "you have no game this week", which
  // is an answer. A snapshot that is still loading, or failed, is not — and
  // drawing "No matchup yet. Draft first." for either of those would tell
  // somebody their league had not drafted because a request was in flight.
  return (
    <>
      {params.id === undefined && games.length === 2 && id !== null ? (
        <div className="fsp-game-switch">
          <Segments
            label="Which of this week's games"
            items={games.map((g) => {
              const them = g.home.team.isMine ? g.away.team : g.home.team;
              return { id: g.id, label: `vs ${them.name}` };
            })}
            value={id}
            onChange={choose}
          />
        </div>
      ) : null}
      <MatchupOfGame id={id} period={period} />
    </>
  );
}

function MatchupOfGame({ id, period }: { id: string | null; period: number | undefined }) {
  const params = useParams();
  return (
    <FromLeague snapshot={useMatchup(id, params.id === undefined ? period : undefined)}>
      {(view) =>
        view === null ? (
          // "No matchup this week", not "Draft first": a drafted league whose
          // season has not opened, and a team on a bye, both land here
          // (AUDIT-2026-09-23 U1, walked on the family league).
          <div className="fsp-matchup-none">
            <Text tone="secondary">No matchup this week.</Text>
          </div>
        ) : (
          // Matchup ids repeat across leagues (m3-1 in each), so the once-ever
          // celebration is keyed by league too.
          <MatchupBody
            view={view}
            celebrationKey={`${params.leagueId ?? 'league'}:${view.id}`}
            // The week's other games: the week on screen, the Scoreboard's own read.
            scoresPeriod={params.id === undefined ? period : view.period}
          />
        )
      }
    </FromLeague>
  );
}

function MatchupBody({
  view,
  celebrationKey,
  scoresPeriod,
}: {
  view: MatchupView;
  celebrationKey: string;
  scoresPeriod: number | undefined;
}) {
  const { play, celebrate } = useDelight();
  const navigate = useNavigate();
  const base = useLeagueBase();
  const chat = useChatDrawer();
  // Band 4 on this screen: the projection column and the game line beside a
  // live score. The score itself is the screen's answer and never hides.
  const showContext = useAtLeast('standard');
  // Projections the league turned off are not served (RULES_SCHEMA §15c), and
  // their column and sums are left out rather than drawn as dashes.
  const tools = useTools();
  // A play's time, in the reader's zone and named, as every time is (B-181).
  const zone = useReaderZone();
  const [card, setCard] = useState<PlayerCardData | null>(null);
  // THE BENCHES, COLLAPSED (MATCHUP.md): one switch opens both sides', so a
  // desktop's two columns open together.
  const [benchOpen, setBenchOpen] = useState(false);
  // A COMMISSIONER'S ADJUSTMENT, once the matchup has started, in a real
  // league (COMMISSIONER_OVERRIDES.md, J1).
  const active = useActiveLeague();
  const canAdjust =
    useLeagueSource() === 'api' && active?.league.isCommissioner === true && view.status !== 'pre';
  const [adjusting, setAdjusting] = useState(false);
  const prev = useRef<MatchupView | null>(null);
  const tier = useTier();
  const phone = tier === 'phone';
  // My lineup's verdict for now, from the read every league screen shares.
  const home = valueOf(useHome());
  // A tablet or desktop opens head to head; somebody who prefers the two
  // lists switches, and this device remembers (B-209).
  const [wideView, setWideView] = useState<WideView>(readWideView);
  const chooseWideView = (next: WideView) => {
    setWideView(next);
    try {
      localStorage.setItem(WIDE_VIEW_KEY, next);
    } catch {
      // Storage blocked: the choice holds for this visit.
    }
  };

  // Sound and celebration on changes (DELIGHT.md).
  useEffect(() => {
    const p = prev.current;
    prev.current = view;
    const myTeamIsHome = view.home.team.isMine;
    const mySide = myTeamIsHome ? 'home' : 'away';
    const theirSide = myTeamIsHome ? 'away' : 'home';

    // The win moment: the first time this final is seen, and only once per
    // matchup, ever. Mine only, and a tie I won on the tiebreaker counts.
    if (view.status === 'final' && !hasCelebrated(celebrationKey)) {
      markCelebrated(celebrationKey);
      if (view[mySide].team.isMine && view.winnerTeamId === view[mySide].team.id) {
        play('win');
        celebrate([
          view[mySide].team.colors.primary,
          view[mySide].team.colors.secondary,
          '#ffffff',
        ]);
      }
    }

    if (!p || p.id !== view.id) return;
    const diff = (side: 'home' | 'away') => view[side].score - p[side].score;
    if (diff(mySide) >= 5) play('score');
    else if (diff(theirSide) >= 5) play('theirScore');
    else if (diff('home') !== 0 || diff('away') !== 0) play('tick');
  }, [view, play, celebrate, celebrationKey]);

  const homeWin = view.winProbabilityHome;
  const meHome = view.home.team.isMine;
  const me = meHome ? view.home : view.away;
  const them = meHome ? view.away : view.home;
  // Null all the way through rather than a default: a win bar drawn from a
  // number nobody computed is a confident-looking lie (E1, 2026-09-19).
  const myProb = homeWin === null ? null : meHome ? homeWin : 1 - homeWin;
  const final = view.status === 'final';
  // AHEAD, OR WON: a final is marked by who won it, which on a tie the league
  // broke is not the higher score (N1, B-232).
  const leads = (side: typeof me, other: typeof me) =>
    final ? view.winnerTeamId === side.team.id : side.score > other.score;
  const tieLine = final ? tieSentence(view, me, them, showContext) : null;
  const medianLine = medianSentence(view, me, them, showContext);
  // ONE BOT SPEAKS ON THE CARD (AI_MANAGERS.md §6.5, B-458): in my game, my
  // opponent; in a game I am not in, the side ahead (the winner, once it is
  // final), or the home side on a level score. Never two speeches.
  const inGame = view.home.team.isMine || view.away.team.isMine;
  // MUTED, NOTHING (B-299): a real league's read already says nothing to a
  // reader who muted the bots; the practice league's is the client's own.
  const hideBots = useAccountPrefs().account?.chat?.hideBots ?? false;
  const speaker = inGame ? them : leads(view.away, view.home) ? view.away : view.home;
  // ITS FACE (B-464): talking before kickoff and on a level score, pleased
  // ahead, annoyed behind.
  const across = speaker === view.home ? view.away : view.home;
  const mood =
    view.status === 'pre'
      ? 'talking'
      : leads(speaker, across)
        ? 'pleased'
        : leads(across, speaker)
          ? 'annoyed'
          : 'talking';

  // EACH SIDE'S FACE (B-488), a person's or a bot's: pleased while its team
  // leads, annoyed while it trails, at rest before kickoff and on a level score.
  const faceOf = (side: typeof me, other: typeof me) =>
    view.status === 'pre'
      ? 'idle'
      : leads(side, other)
        ? 'pleased'
        : leads(other, side)
          ? 'annoyed'
          : 'idle';

  const header = (
    // Where a game's card lands when it grows into its matchup (B-409).
    <div className="fsp-surface fsp-mcard fsp-hero-target">
      <div className="fsp-mcard-sides">
        <SideHeader
          side={me}
          winning={leads(me, them)}
          mood={faceOf(me, them)}
          // Their roster, as MATCHUP.md says a team's name opens (2026-09-24).
          onOpen={() => navigate(`${base}/teams/${me.team.id}`)}
        />
        <SideHeader
          side={them}
          winning={leads(them, me)}
          mood={faceOf(them, me)}
          right
          onOpen={() => navigate(`${base}/teams/${them.team.id}`)}
        />
      </div>
      <div className="fsp-mcard-foot">
        {myProb === null ? null : (
          <SplitBar share={myProb} left={me.team.colors.primary} right={them.team.colors.primary} />
        )}
        <div className="fsp-mcard-status">
          <span className="fsp-mcard-marks">
            {view.status === 'live' && view.stats.state !== 'live' ? (
              // Under way, and its numbers arrive overnight: not "Live"
              // (BACKLOG L2). The note below says when they last did.
              <span className="fsp-mcard-final">In progress</span>
            ) : view.status === 'live' ? (
              <LiveDot label={view.liveLabel ? `Live · ${view.liveLabel}` : 'Live'} />
            ) : view.status === 'final' ? (
              <span className="fsp-mcard-final">FINAL · {finalLine(view, me, them)}</span>
            ) : (
              <span className="fsp-muted">Kickoff {firstKickoff(view) ?? 'soon'}</span>
            )}
            {/* The chance is the left side's, so it sits on the left, under
                that side's share of the bar (B-170). */}
            {myProb === null || view.status === 'final' ? null : (
              <Detail min="pro">
                <span className="fsp-muted">
                  · <span className="fsp-num fsp-mcard-chance">{Math.round(myProb * 100)}%</span> to
                  win
                </span>
              </Detail>
            )}
          </span>
        </div>
        {/* When the numbers were last updated, so a 0 on the board is not
            read as a score (BACKLOG L2). */}
        <StatsNote stats={view.stats} />
        {view.rivalry ? (
          <div className="fsp-sub fsp-rivalry">
            <Term tip="The all-time series between these two teams, playoffs included.">
              {view.rivalry.line}
            </Term>
          </div>
        ) : null}
        {/* A CHARACTER SPEAKING, so whole at every level, like a chat line:
            its name in its team's readable color, as chat draws a name, then
            the line in quotes. */}
        {speaker.botLine !== null && !hideBots ? (
          <div className="fsp-sub fsp-ui-wrap-text fsp-matchup-botline">
            <TeamMark team={speaker.team} size={22} face={mood} />{' '}
            <Ink color={readableColor(speaker.team.colors)}>{speaker.botLine.name}:</Ink> “
            {speaker.botLine.text}”
          </div>
        ) : null}
        {/* WHY A LEVEL FINAL ENDED AS IT DID (N1, B-232): one sentence at
            every level, Standard adding the benches it compared. */}
        {tieLine !== null ? (
          <div className="fsp-sub fsp-ui-wrap-text fsp-matchup-tie">{tieLine}</div>
        ) : null}
        {/* THE WEEK'S SECOND GAME, against the league median (Q3, B-359):
            one sentence at every level, Standard adding the median itself. */}
        {medianLine !== null ? (
          <div className="fsp-sub fsp-ui-wrap-text fsp-matchup-median">{medianLine}</div>
        ) : null}
        {/* WHY A SCORE IS NOT ITS ROWS' SUM (J1): one sentence at every
            level, and Standard adds its numbers and reasons to it — Simple
            removes numbers and never has a sentence of its own (B-40; the
            review of J1, 2.1). */}
        {[me, them]
          .filter((s) => s.adjustments.length > 0)
          .map((s) => (
            <div key={s.team.id} className="fsp-sub fsp-ui-wrap-text fsp-matchup-adjusted">
              {`${s.team.name}’s score includes the commissioner’s adjustment`}
              {showContext
                ? `: ${s.adjustments
                    .map((a) => `${a.points > 0 ? '+' : ''}${a.points}, “${a.reason}”`)
                    .join('; ')}`
                : ''}
              .
            </div>
          ))}
        {canAdjust ? (
          <div className="fsp-matchup-adjust">
            <Button variant="quiet" size="xs" icon="veto" onClick={() => setAdjusting(true)}>
              Adjust points
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );

  const openCard = (r: MatchupRowView) =>
    r.player &&
    setCard({
      player: r.player,
      game: r.game,
      projection: r.projection,
      points: r.points,
      statLine: r.statLine,
      action: null,
    });
  const h2h = <HeadToHead me={me} them={them} onOpen={openCard} short={phone} final={final} />;

  /**
   * What the rows that are not still to play are: games begun, and slots with
   * no game (an empty one, a bye), which are counted already too. "Kicked
   * off" over a bye before any kickoff was untrue (the review of 2026-10-11).
   */
  // NOT PLAYING, not "no game": a starter ruled out has a game and sits
  // here too (B-521), beside a bye and an empty slot.
  const doneHeading = (done: readonly MatchupRowView[]): string => {
    const begun = done.some((r) => r.game?.state === 'live' || r.game?.state === 'final');
    const none = done.some((r) => r.game?.state !== 'live' && r.game?.state !== 'final');
    return begun && none
      ? 'Kicked off or not playing'
      : begun
        ? 'Kicked off'
        : 'Not playing this week';
  };
  const rows = (side: typeof me) => {
    // The players on the bench; an open bench spot is nobody to show.
    const benchers = side.bench.filter((r) => r.player !== null);
    // A starter ruled out is not yet to play (B-521), as the count says.
    const done = side.rows.filter((r) => r.game?.state !== 'pre' || r.ruledOut);
    const pending = side.rows.filter((r) => r.game?.state === 'pre' && !r.ruledOut);
    // A BENCH ROW is not in the score: a settled week's empty or bye bench
    // spot is not a 0 the total holds, as a starter's is.
    const row = (r: MatchupRowView, i: number, benched = false) => (
      <div
        key={`${r.slot}-${i}`}
        className={`fsp-row fsp-row-clickable fsp-matchup-row ${showContext ? 'fsp-matchup-row-context' : ''} ${r.lastDelta && recent(r.lastDelta.at, view.lastUpdatedAt) ? 'fsp-flash' : ''}`}
        // THE NAME IS THE BUTTON; THE ROW IS A BIGGER TARGET FOR IT (B-275).
        // The row was a button with buttons inside it, which a screen reader
        // cannot tell apart (UX_STANDARDS UX-2).
        data-ux-convenience=""
        onClick={() => openCard(r)}
      >
        <PositionPill pos={r.slot} size="sm" />
        {r.player ? (
          <div className="fsp-matchup-who">
            <div className="fsp-name">
              <button
                type="button"
                className="fsp-linklike fsp-name-text fsp-name-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openCard(r);
                }}
              >
                {r.player.name}
              </button>
              <ProTeamCode code={r.player.proTeam} />
              {/* A BYE ONCE (B-521): the game's line says it where it is
                  shown, the badge where it is not. */}
              <StatusBadge player={showContext ? { ...r.player, onBye: false } : r.player} />
              {r.lastDelta &&
              Math.abs(r.lastDelta.points) >= 5 &&
              recent(r.lastDelta.at, view.lastUpdatedAt, 20 * 60 * 1000) ? (
                <DeltaChip points={r.lastDelta.points} label={r.lastDelta.label} />
              ) : null}
            </div>
            {/* A starter whose game came and went without him says so at
                every level (B-233): the 0 beside him is not a bad day. */}
            {r.noScore === 'did-not-play' ? (
              <div className="fsp-sub fsp-matchup-noscore">
                Did not play
                {showContext ? (
                  <>
                    {' · '}
                    <GameLabel game={r.game} />
                  </>
                ) : null}
              </div>
            ) : showContext ? (
              <div className="fsp-sub">{r.statLine ?? <GameLabel game={r.game} />}</div>
            ) : null}
            {projectionNote(r.projection, r.basis) !== null && r.game?.state !== 'bye' ? (
              <div className="fsp-sub fsp-muted fsp-ui-wrap-text">
                {projectionNote(r.projection, r.basis)}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="fsp-muted">Empty</div>
        )}
        <div
          className={`fsp-right fsp-num fsp-matchup-points ${r.points !== null ? 'scored' : ''}`}
        >
          {r.points !== null ? (
            <PointsWhy
              player={r.player}
              points={r.points}
              breakdown={r.breakdown}
              statLine={r.statLine}
            >
              {r.points.toFixed(1)}
            </PointsWhy>
          ) : // A SETTLED WEEK COUNTED AN EMPTY SLOT AND A BYE AS 0 (N1,
          // B-233), so the row says 0 rather than a dash beside a total
          // that already holds it.
          final && r.noScore !== null && !benched ? (
            (0).toFixed(1)
          ) : // At Simple a row whose game is still to come has nothing to
          // show: a column of dashes before kickoff is a column nobody
          // can fill (plan rule 8). A dash is kept for a game under way
          // without its line, which the screen's note explains (B-172).
          (!showContext || !tools.projections) && r.game?.state === 'pre' ? null : (
            '—'
          )}
        </div>
        {showContext && tools.projections ? (
          <div className="fsp-right fsp-num fsp-sub">
            {/* Labeled on every row (B-295): only "Yet to play" has a heading
                that says proj, and the rows that kicked off sit above it. */}
            <Proj>
              <ProjectionWhy player={r.player} projection={r.projection} basis={r.basis}>
                {num(r.projection)}
              </ProjectionWhy>
            </Proj>
          </div>
        ) : null}
      </div>
    );
    return (
      <div>
        {/* THE OTHER HALF OF "YET TO PLAY", named: the starters whose games
            have begun sat above that heading with none of their own (the
            page-by-page review of 2026-10-11). */}
        {done.length && pending.length ? (
          <div className="fsp-region-head fsp-region-head-static fsp-matchup-pending">
            <span className="fsp-cap">{doneHeading(done)}</span>
          </div>
        ) : null}
        {done.map((r, i) => row(r, i))}
        {pending.length ? (
          <>
            <div className="fsp-region-head fsp-region-head-static fsp-matchup-pending">
              <span className="fsp-cap">Yet to play</span>
              {/* Standard's number, as the header's projected final is. */}
              {tools.projections ? (
                <Detail min="standard">
                  <span className="fsp-cap fsp-num">
                    {/* One unprojected starter makes the sum unknowable, not smaller. */}
                    proj{' '}
                    {pending.some((r) => r.projection === null)
                      ? num(null)
                      : num(pending.reduce((sum, r) => sum + (r.projection ?? 0), 0))}
                  </span>
                </Detail>
              ) : null}
            </div>
            {pending.map((r, i) => row(r, i))}
          </>
        ) : null}
        {/* THE BENCH, COLLAPSED UNDER ITS NAME at every level (MATCHUP.md,
            DETAIL_LEVELS rung 5), each player a row as a starter's is once
            opened; its total is Everything's, the bench comparison
            (DETAIL_LEVELS §Matchup). A league's real matchup served only
            the total until 2026-10-17. */}
        {benchers.length > 0 ? (
          <>
            <button
              type="button"
              className="fsp-region-head fsp-region-head-static fsp-matchup-bench-toggle"
              aria-expanded={benchOpen}
              onClick={() => setBenchOpen((o) => !o)}
            >
              <Icon name={benchOpen ? 'expand' : 'next'} size="sm" />
              <span className="fsp-cap">Bench</span>
              <span className="fsp-sub">
                {benchers.length} {benchers.length === 1 ? 'player' : 'players'}
              </span>
              <Detail min="pro">
                <span className="fsp-cap fsp-num fsp-matchup-bench-total">
                  {side.benchPoints === null ? '—' : side.benchPoints.toFixed(1)}
                </span>
              </Detail>
            </button>
            {benchOpen ? benchers.map((r, i) => row(r, i, true)) : null}
          </>
        ) : (
          <Detail min="pro">
            <div className="fsp-sub fsp-matchup-bench">
              <Term tip={GLOSSARY.bench}>Bench</Term>{' '}
              <span className="fsp-num">
                {side.benchPoints === null ? '—' : side.benchPoints.toFixed(1)}
              </span>
            </div>
          </Detail>
        )}
      </div>
    );
  };

  const meTitle = me.team.isMine ? 'Me' : me.team.abbreviation;

  // A PLAY'S PLAYER IS ONE OF THE ROWS, whose club rides beside his name
  // there and here (B-510): the play itself carries only his id.
  const clubOf = new Map(
    [view.home, view.away]
      .flatMap((s) => [...s.rows, ...s.bench])
      .flatMap((r) => (r.player === null ? [] : [[r.player.id, r.player.proTeam] as const])),
  );
  const plays = (
    <div>
      {view.plays.length === 0 ? (
        <div className="fsp-matchup-none">
          <Text tone="secondary">Nothing yet. Plays show up here as they happen.</Text>
        </div>
      ) : (
        view.plays.map((p) => (
          <div key={p.id} className="fsp-row fsp-matchup-play">
            <div className="fsp-matchup-who">
              <div className="fsp-matchup-play-line">
                <Text weight="strong">
                  <PlayerName
                    player={{ id: p.playerId, name: p.playerName }}
                    {...(clubOf.has(p.playerId) ? { club: clubOf.get(p.playerId)! } : {})}
                  />
                </Text>{' '}
                {p.description}
              </div>
              <div className="fsp-sub">
                {p.side === (meHome ? 'home' : 'away') ? (
                  'you'
                ) : (
                  <TeamName team={them.team}>{them.team.ownerName}</TeamName>
                )}
                {zone !== null
                  ? ` · ${formatInZone(p.at, zone, { hour: 'numeric', minute: '2-digit' })}`
                  : null}
              </div>
            </div>
            <DeltaChip points={p.points} />
          </div>
        ))
      )}
    </div>
  );

  // The talk is the league's chat, one tap away rather than a second copy of it.
  // SHARE THIS GAME (PLAN-2026-10-31-players-and-games-in-chat §1): the
  // game, attached to the next line in the chat that opens, with its score
  // as it stands when that line is sent. A real league's alone.
  const week = valueOf(useScoreboard(view.period))?.period.label ?? `Week ${view.period}`;
  const talk = (
    <div className="fsp-matchup-none">
      <Text tone="secondary" block>
        Talk it over in the league’s chat.
      </Text>
      <div className="fsp-matchup-talk">
        <Button size="xs" icon="chat" onClick={() => chat.setOpen(true)}>
          Open chat
        </Button>
        {chat.canShare ? (
          <Button
            size="xs"
            icon="share"
            onClick={() =>
              chat.attach({
                share: { kind: 'matchup', matchupId: view.id },
                label: gameLabel(week, view.home.team.name, view.away.team.name),
              })
            }
          >
            Share this game
          </Button>
        ) : null}
      </div>
    </div>
  );

  // THE LINEUP CHECK (B-317): while a slot of mine still cannot score this
  // week — an Out, a bye, an empty slot — the health bar says so under the
  // score, and its button opens My Team on that slot (D2). Inactives land on
  // the day, and this is where somebody following the game sees one. Absent
  // when the lineup is fine: the card already says when it kicks off.
  const health = home?.health ?? null;
  const check =
    me.team.isMine &&
    health !== null &&
    home?.period.index === view.period &&
    (health.status === 'warn' || health.status === 'error') ? (
      <div className="fsp-gd-check">
        <HealthBar health={health} linkToLineup />
      </div>
    ) : null;

  // WHO PLAYS WHEN, AND THE REST OF THE LEAGUE (B-318). A large screen shows
  // the two together in the side column; narrower, each is its own tab.
  const large = tier === 'large';
  const games = <GameSlate me={me} them={them} onOpen={openCard} />;
  const scores = <LeagueScores period={scoresPeriod} exclude={view.id} />;
  const gamesAndScores = large ? (
    <>
      {games}
      <div className="fsp-gd-section">
        <Caption>Around the league</Caption>
      </div>
      {scores}
    </>
  ) : (
    games
  );
  const sideTabs = [
    { id: 'games', title: 'Games', content: gamesAndScores },
    ...(large ? [] : [{ id: 'scores', title: 'Scores', content: scores }]),
    { id: 'plays', title: 'Plays', content: plays },
    { id: 'talk', title: 'Talk', content: talk },
  ];

  return (
    <>
      <SplitScreen
        status={
          phone ? (
            <>
              {header}
              {check}
            </>
          ) : (
            <>
              {header}
              {check}
              <div className="fsp-matchup-view">
                <Segments
                  label="How to show the matchup"
                  items={[
                    { id: 'h2h', label: 'Head to head' },
                    { id: 'lists', label: 'Two lists' },
                  ]}
                  value={wideView}
                  onChange={chooseWideView}
                />
              </div>
            </>
          )
        }
        // Head to head leads, and the rest are the side column's tabs. With
        // the two lists the template keeps two columns (DIRECTION U21): mine
        // leads and theirs is the side column's first tab, half and half; a
        // tablet's two lists stay side by side, the rest behind the handle.
        lead={!phone && wideView === 'h2h'}
        regions={
          phone
            ? // A phone: the matchup first, then the day around it.
              [
                { id: 'vs', title: 'Matchup', content: h2h },
                { id: 'games', title: 'Games', content: games },
                { id: 'scores', title: 'Scores', content: scores },
                { id: 'plays', title: 'Plays', content: plays },
                { id: 'talk', title: 'Talk', content: talk },
              ]
            : wideView === 'h2h'
              ? [{ id: 'vs', title: 'Head to head', content: h2h }, ...sideTabs]
              : [
                  { id: 'me', title: meTitle, content: rows(me) },
                  { id: 'them', title: 'Them', content: rows(them) },
                  ...sideTabs,
                ]
        }
        ratio={[5, 5]}
      />
      <PlayerCard data={card} onClose={() => setCard(null)} />
      {canAdjust && active !== null ? (
        <AdjustPointsSheet
          target={{
            period: view.period,
            sides: [view.home, view.away].map((s) => ({
              matchupId: view.id,
              team: s.team,
              score: s.score,
            })),
          }}
          leagueId={active.league.id}
          opened={adjusting}
          onClose={() => setAdjusting(false)}
        />
      ) : null}
    </>
  );
}

/**
 * The label of the first game still to start, across both sides' starters.
 * Not the home side's first row: that is whichever slot comes first, and a
 * Sunday quarterback there said "Kickoff Sun" over a Thursday starter.
 */
function firstKickoff(view: MatchupView): string | null {
  let first: MatchupRowView['game'] = null;
  for (const r of [...view.home.rows, ...view.away.rows]) {
    const g = r.game;
    if (g === null || g.state !== 'pre' || g.startAt === null) continue;
    if (first === null || first.startAt === null || g.startAt < first.startAt) first = g;
  }
  return first?.label ?? null;
}

type Side = MatchupView['home'];

const winnerAmong = (view: MatchupView, me: Side, them: Side): Side | null =>
  view.winnerTeamId === me.team.id ? me : view.winnerTeamId === them.team.id ? them : null;

/**
 * A final's verdict (N1, B-232): won or lost, and by how much or on which
 * tiebreaker; "tied" for a tie. From my side when I play in it, and naming
 * the team that won when I do not. It said "lost by 0.0" over a tie, and
 * "lost" over a tie the league gave me on bench points.
 */
function finalLine(view: MatchupView, me: Side, them: Side): string {
  const winner = winnerAmong(view, me, them);
  if (winner === null) return 'tied';
  const how =
    me.score === them.score
      ? settledOn(view.decidedBy)
      : `by ${Math.abs(me.score - them.score).toFixed(1)}`;
  if (me.team.isMine) return `${winner === me ? 'won' : 'lost'} ${how}`;
  return `${winner.team.abbreviation} won ${how}`;
}

/**
 * Each side's game against the league median (scenario Q3, B-359): where it
 * stands while the week is played, and its result once it is final. One
 * sentence at every level; Standard adds the median — Simple removes numbers
 * and never has a sentence of its own (B-40). Null for a week with no median.
 */
function medianSentence(view: MatchupView, me: Side, them: Side, numbers: boolean): string | null {
  const m = view.median;
  if (m === null) return null;
  const meHome = view.home.team.id === me.team.id;
  const mine = meHome ? m.home : m.away;
  const theirs = meHome ? m.away : m.home;
  const who = (side: Side) => (side.team.isMine ? 'you' : side.team.name);
  const median = numbers ? ` ${m.score.toFixed(1)}` : '';
  if (view.status === 'final') {
    const result = (x: typeof mine) =>
      x === 'above' ? 'a win' : x === 'below' ? 'a loss' : 'a tie';
    return `League median${median}: ${result(mine)} for ${who(me)}, ${result(theirs)} for ${who(them)}.`;
  }
  const place = (x: typeof mine) =>
    x === 'above' ? 'above it' : x === 'below' ? 'below it' : 'level with it';
  const verb = (side: Side) => (side.team.isMine ? 'are' : 'is');
  return `League median so far${median}: ${who(me)} ${verb(me)} ${place(mine)}, ${who(them)} ${verb(them)} ${place(theirs)}.`;
}

/**
 * Why a level final ended as it did (N1, B-232): one sentence at every level,
 * and Standard adds the benches a bench-points tiebreak compared — Simple
 * removes numbers and never has a sentence of its own (B-40). Null for a final
 * that was not level.
 */
function tieSentence(view: MatchupView, me: Side, them: Side, numbers: boolean): string | null {
  if (me.score !== them.score) return null;
  const who = (s: Side) => (s.team.isMine ? 'you' : s.team.name);
  const winner = winnerAmong(view, me, them);
  if (winner === null) {
    if (view.tieRule === 'bench-points') {
      const bench = me.benchPoints;
      return `Tied, and the benches were level too, so it stays a tie${
        numbers && bench !== null ? `: ${bench.toFixed(1)} each` : ''
      }.`;
    }
    return 'Tied, and a tie stays a tie in this league.';
  }
  const loser = winner === me ? them : me;
  switch (view.decidedBy) {
    case 'bench-points': {
      // Tenths, unless the benches only part at the hundredth: "30.1 to
      // 30.1" under a win on bench points says it was level (the review of N1).
      const [w, l] = [winner.benchPoints, loser.benchPoints];
      const places = w !== null && l !== null && w.toFixed(1) === l.toFixed(1) ? 2 : 1;
      return `Tied, so it went to bench points, and ${who(winner)} had more on the bench${
        numbers && w !== null && l !== null ? `: ${w.toFixed(places)} to ${l.toFixed(places)}` : ''
      }.`;
    }
    case 'home-team':
      return `Tied, so it went to the home team, and that was ${who(winner)}.`;
    case 'coin-flip':
      return `Tied, so it went to a coin flip, and ${who(winner)} won it.`;
    case 'higher-seed':
      return `Tied, so it went to the higher seed, and that was ${who(winner)}.`;
    default:
      return `Tied, and the tiebreaker went to ${who(winner)}.`;
  }
}

const CELEBRATED_KEY = storageKey('celebrated.v1');

/** A tablet's or desktop's matchup view (B-209). */
type WideView = 'h2h' | 'lists';
const WIDE_VIEW_KEY = storageKey('matchup.view.v1');

function readWideView(): WideView {
  try {
    return localStorage.getItem(WIDE_VIEW_KEY) === 'lists' ? 'lists' : 'h2h';
  } catch {
    return 'h2h';
  }
}

function hasCelebrated(id: string): boolean {
  try {
    return (JSON.parse(localStorage.getItem(CELEBRATED_KEY) ?? '[]') as string[]).includes(id);
  } catch {
    return false;
  }
}

function markCelebrated(id: string): void {
  try {
    const ids = JSON.parse(localStorage.getItem(CELEBRATED_KEY) ?? '[]') as string[];
    localStorage.setItem(CELEBRATED_KEY, JSON.stringify([...ids, id].slice(-50)));
  } catch {
    // Storage blocked: we may celebrate again next time. Acceptable.
  }
}

function recent(atIso: string, nowIso: string, windowMs = 90 * 1000): boolean {
  return Date.parse(nowIso) - Date.parse(atIso) < windowMs;
}

function SideHeader({
  side,
  winning,
  mood,
  right,
  onOpen,
}: {
  side: MatchupView['home'];
  winning: boolean;
  /** What the team's face is doing, when it has one (B-488). */
  mood: FaceMood;
  right?: boolean;
  /** Tapping the team opens their roster. */
  onOpen?: () => void;
}) {
  // THE CHARACTERS FACE OFF across the middle from a tablet up (§4): a phone's
  // half is its score's.
  const figure = teamFace(side.team) !== null;
  const tools = useTools();
  // EACH SIDE ITS OWN TEAM'S BANNER (PLAN-2026-10-24-team-banners §4), the
  // other side's mirrored, so the two scenes meet behind the two characters.
  const banner = hasBanner(side.team);
  return (
    <Colored
      className={`fsp-mcard-side fsp-matchup-side ${right ? 'right' : ''}${figure ? ' has-figure' : ''}${banner ? ' fsp-has-banner' : ''}`}
      colors={{ '--team': side.team.colors.primary }}
      data-tone={side.team.isMine ? 'mine' : undefined}
      // Which side is ahead, for a look to mark (the matchup card does the same).
      data-lead={winning ? 'true' : undefined}
    >
      {banner ? <TeamBanner team={side.team} mirror={right} /> : null}
      <div className="fsp-mcard-head">
        {!right ? <TeamMark team={side.team} face={mood} /> : null}
        <div className="fsp-mcard-who">
          <button
            type="button"
            className="fsp-linklike fsp-matchup-team"
            onClick={onOpen}
            title={`${side.team.ownerName}'s roster`}
          >
            <span className="fsp-matchup-team-name">{side.team.name}</span>
            {side.team.titles ? <TitleCrown titles={side.team.titles} /> : null}
          </button>
          <div className="fsp-sub">
            {/* Ties when there are any, as the matchup card writes a record (N1). */}
            {side.team.record.wins}–{side.team.record.losses}
            {side.team.record.ties ? `–${side.team.record.ties}` : ''} · {side.team.ownerName}
          </div>
        </div>
        {right ? <TeamMark team={side.team} face={mood} /> : null}
      </div>
      <div className="fsp-matchup-score">
        <ScoreNumber
          value={side.score}
          size={52}
          color={winning ? 'var(--fsp-ink-primary)' : 'var(--fsp-ink-secondary)'}
        />
      </div>
      <div className="fsp-sub fsp-mcard-proj">
        {side.yetToPlay} yet to play
        {/* THE PROJECTED FINAL IS STANDARD'S (DETAIL_LEVELS §Matchup): Simple
            is both scores and how many are yet to play. It read "proj —" at
            every level until N3 filled it, and then a number Simple does not
            carry (round 3, N6). */}
        {/* None while the league keeps no projections (RULES_SCHEMA §15c). */}
        {tools.projections ? (
          <Detail min="standard">
            {' '}
            · proj{' '}
            <Why
              subject={`${side.team.name} projected`}
              value={num(side.projected)}
              rows={[
                { label: 'On the board', value: side.score.toFixed(1) },
                { label: 'Still to come', value: diffOf(side.projected, side.score) },
                { label: 'Yet to play', value: String(side.yetToPlay), note: 'starters' },
                { label: 'In progress', value: String(side.inProgress), note: 'starters' },
              ]}
              footnote="A projection. Nobody knows the future."
            >
              <span className="fsp-num">{num(side.projected)}</span>
            </Why>
          </Detail>
        ) : null}
      </div>
      {figure ? (
        <TeamFigure team={side.team} mood={mood} height={132} className="fsp-matchup-figure" />
      ) : null}
    </Colored>
  );
}
